/**
 * Favourite locations, held on THIS DEVICE (FR-SEL-002, and FR-SEL-003's
 * device half; seam L-UI1).
 *
 * No server model holds a favourite, and this wave allows no schema, so they
 * live in `localStorage` under `paio-ordering-ui:favourites:<brandNodeId>` as
 * `{"v":1,"ids":[...]}`, most recently used first, at most 50 (the server's
 * `ids` limit). A later server-side favourites model may import this key once
 * and then delete it.
 *
 * The `kitchen/deviceStore.ts` pattern: storage can be absent or throw
 * (private mode, blocked site data), so every access is guarded and a failure
 * reads as "nothing stored". An unreadable value is treated as empty, warned
 * about by key, and overwritten on the next write.
 *
 * Every mutation RETURNS the list it meant to store, so a caller whose storage
 * refused the write still shows the diner what they just did for as long as
 * the page is open.
 */

export const MAX_FAVOURITES = 50;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export interface FavouriteStore {
  read(): readonly string[];
  /** Marks a location, at the front. */
  add(locationNodeId: string): readonly string[];
  remove(locationNodeId: string): readonly string[];
  /** Moves an existing favourite to the front; a non-favourite is left alone. */
  touch(locationNodeId: string): readonly string[];
}

export function favouriteKey(brandNodeId: string): string {
  return `paio-ordering-ui:favourites:${brandNodeId.toLowerCase()}`;
}

function defaultStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function parse(raw: string, key: string): readonly string[] {
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value === "object" && value !== null && "v" in value && "ids" in value) {
      const { v, ids } = value;
      if (v === 1 && Array.isArray(ids) && ids.every((id) => typeof id === "string" && UUID.test(id))) {
        return ids.slice(0, MAX_FAVOURITES);
      }
    }
  } catch {
    // Falls through to the warning below.
  }
  console.warn(`[locations] ignoring unreadable favourites at ${key}; it is replaced on the next change`);
  return [];
}

export function createFavouriteStore(
  brandNodeId: string,
  backing: () => Storage | null = defaultStorage,
): FavouriteStore {
  const key = favouriteKey(brandNodeId);

  function read(): readonly string[] {
    let raw: string | null = null;
    try {
      raw = backing()?.getItem(key) ?? null;
    } catch {
      return [];
    }
    return raw === null ? [] : parse(raw, key);
  }

  function write(ids: readonly string[]): readonly string[] {
    const kept = ids.slice(0, MAX_FAVOURITES);
    try {
      backing()?.setItem(key, JSON.stringify({ v: 1, ids: kept }));
    } catch {
      // Favourites simply do not persist on this device.
    }
    return kept;
  }

  return {
    read,
    add(locationNodeId) {
      const id = locationNodeId.toLowerCase();
      return write([id, ...read().filter((existing) => existing !== id)]);
    },
    remove(locationNodeId) {
      const id = locationNodeId.toLowerCase();
      return write(read().filter((existing) => existing !== id));
    },
    touch(locationNodeId) {
      const id = locationNodeId.toLowerCase();
      const current = read();
      return current.includes(id) ? write([id, ...current.filter((existing) => existing !== id)]) : current;
    },
  };
}
